// Onboarding wizard persistence (US-038).
//
// Wizard state is stored server-side in `users.onboarding_progress` (a JSON
// column added by the 0001 migration). Reads + writes funnel through the
// `useOnboardingState` + `useUpdateOnboarding` hooks from
// `@barel/api-client/react`.
//
// Design notes:
//
// - We mirror server state into an in-memory snapshot the wizard can drive
//   synchronously. Server writes are debounced (250ms) so each click in a
//   step doesn't slam the API.
// - LocalStorage is *not* used for the canonical wizard state — that lives
//   on the server. We do persist `dismissedFtuBanner` in localStorage since
//   it's a per-device preference, not user state.
// - The shape is forward-compatible: every field optional, only `step` is
//   guaranteed by the server-side merge logic.

import type { OnboardingProgress, OnboardingStateResponse } from '@barel/api-client/react';

export const WIZARD_STEPS = [1, 2, 3, 4, 5, 6] as const;
export const WIZARD_TOTAL = 6;

/** Merge a server snapshot with a local patch, clamping `step` to [1, 6]. */
export function mergeProgress(
  prev: OnboardingProgress | null,
  patch: Partial<OnboardingProgress>,
): OnboardingProgress {
  const merged: OnboardingProgress = {
    step: prev?.step ?? 1,
    ...prev,
    ...patch,
  };
  if (typeof merged.step !== 'number' || merged.step < 1) merged.step = 1;
  if (merged.step > WIZARD_TOTAL) merged.step = WIZARD_TOTAL;
  return merged;
}

/** Initial progress for users with no server-side wizard state. */
export function initialProgress(): OnboardingProgress {
  return { step: 1 };
}

/** Pick the resume step from a server payload. */
export function resumeStep(state: OnboardingStateResponse | undefined): number {
  if (!state || !state.progress) return 1;
  const step = state.progress.step;
  if (typeof step !== 'number') return 1;
  return Math.max(1, Math.min(WIZARD_TOTAL, Math.floor(step)));
}

/** Has onboarding been completed on the server? */
export function isOnboardingComplete(state: OnboardingStateResponse | undefined): boolean {
  return !!state?.completedAt;
}

// ── FTU banner dismissal (per-device, localStorage) ──────────────────────

const FTU_BANNER_DISMISSED_KEY = 'barel:ftu:welcome-banner:dismissed';

export function readFtuBannerDismissed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(FTU_BANNER_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeFtuBannerDismissed(value: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (value) {
      window.localStorage.setItem(FTU_BANNER_DISMISSED_KEY, '1');
    } else {
      window.localStorage.removeItem(FTU_BANNER_DISMISSED_KEY);
    }
  } catch {
    // Storage quota / private mode — ignore.
  }
}

// ── FTU checklist collapse state (per-device) ────────────────────────────

const FTU_CHECKLIST_COLLAPSED_KEY = 'barel:ftu:checklist:collapsed';

export function readFtuChecklistCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(FTU_CHECKLIST_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeFtuChecklistCollapsed(value: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (value) {
      window.localStorage.setItem(FTU_CHECKLIST_COLLAPSED_KEY, '1');
    } else {
      window.localStorage.removeItem(FTU_CHECKLIST_COLLAPSED_KEY);
    }
  } catch {
    // ignore
  }
}
